import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getEmployeeTrackingData } from '../utils/api';
import { CheckCircle, Clock, Video, Briefcase, Calendar, Check, AlertCircle } from 'lucide-react';

export default function EmployeeTrackingPage() {
	const { token } = useParams();
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const getMonthsList = () => {
		const list = [];
		const date = new Date();
		for (let i = 0; i < 12; i++) {
			const year = date.getFullYear();
			const month = String(date.getMonth() + 1).padStart(2, '0');
			const label = date.toLocaleDateString('ar-EG', { year: 'numeric', month: 'long' });
			list.push({ value: `${year}-${month}`, label });
			date.setMonth(date.getMonth() - 1);
		}
		return list;
	};

	const months = getMonthsList();
	const [selectedMonth, setSelectedMonth] = useState(() => {
		const now = new Date();
		return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
	});

	useEffect(() => {
		setLoading(true);
		getEmployeeTrackingData(token, selectedMonth)
			.then(res => {
				setData(res);
				setLoading(false);
			})
			.catch(err => {
				setError(err.response?.data?.error || 'رابط المتابعة غير صحيح أو منتهي الصلاحية');
				setLoading(false);
			});
	}, [token, selectedMonth]);

	const formatSeconds = (totalSeconds) => {
		if (!totalSeconds) return '0:00';
		const mins = Math.floor(totalSeconds / 60);
		const secs = totalSeconds % 60;
		return `${mins}:${secs.toString().padStart(2, '0')}`;
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-gray-50 dark:bg-dark-deeper text-dark dark:text-gray-100 flex items-center justify-center p-4" dir="rtl">
				<div className="flex flex-col items-center gap-4">
					<img src="/logos/ms-media-icon.svg" alt="MS MEDIA" className="h-12 opacity-80" />
					<div className="w-8 h-8 border-4 border-gray-200 dark:border-white/10 border-t-primary rounded-full animate-spin"></div>
					<p className="text-gray-500 dark:text-gray-400 font-medium">جاري تحميل بيانات السجل...</p>
				</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="min-h-screen bg-gray-50 dark:bg-dark-deeper text-dark dark:text-gray-100 flex items-center justify-center p-4" dir="rtl">
				<div className="bg-white dark:bg-dark-deeper p-8 rounded-2xl max-w-sm w-full text-center shadow-xl shadow-red-900/5">
					<div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
						<AlertCircle size={32} />
					</div>
					<h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-2">عذراً</h2>
					<p className="text-gray-500 dark:text-gray-400 mb-6">{error}</p>
				</div>
			</div>
		);
	}

	const { employee, deliverables, stats } = data;
	const totalSecs = stats.total_seconds || 0;
	const baseline = employee.baseline_seconds || 0;
	const remSecs = Math.max(0, baseline - totalSecs);
	const extraSecs = Math.max(0, totalSecs - baseline);
	const progress = employee.track_seconds && baseline > 0 ? Math.min((totalSecs / baseline) * 100, 100) : 0;

	return (
		<div className="min-h-screen bg-[#F8FAFC] dark:bg-dark-deeper text-dark dark:text-gray-100" dir="rtl">
			{/* Header */}
			<div className="bg-white dark:bg-dark-deeper border-b border-gray-200 dark:border-white/10 sticky top-0 z-30 shadow-sm">
				<div className="max-w-5xl mx-auto px-4 py-4 md:py-6 sm:px-6 flex items-center justify-between">
					<div className="flex items-center gap-4">
						<img src="/logos/ms-media-icon.svg" alt="MS MEDIA" className="h-10 hidden sm:block" />
						<div className="h-10 w-0.5 bg-gray-200 dark:bg-white/10 hidden sm:block mx-1"></div>
						<div>
							<h1 className="text-xl font-black text-dark dark:text-gray-100 tracking-tight">بوابة الموظفين</h1>
							<p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1 font-medium"><Check size={14} className="text-success" /> ارتباط آمن ومشفر</p>
						</div>
					</div>

					<div className="flex items-center gap-2 bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-xl px-3 py-1.5 shadow-sm">
						<Calendar size={16} className="text-gray-400" />
						<select
							value={selectedMonth}
							onChange={(e) => setSelectedMonth(e.target.value)}
							className="bg-transparent border-0 text-sm font-bold text-gray-700 dark:text-gray-200 focus:ring-0 outline-none cursor-pointer pr-6 py-0"
						>
							{months.map((m) => (
								<option key={m.value} value={m.value} className="bg-white dark:bg-dark-deeper text-dark dark:text-gray-100">
									{m.label}
								</option>
							))}
						</select>
					</div>
				</div>
			</div>

			<div className="max-w-5xl mx-auto px-4 py-8 sm:px-6 space-y-8 animate-fade-in">
				{/* Greeting */}
				<div className="bg-white dark:bg-dark-deeper p-6 md:p-8 rounded-3xl border border-gray-200 dark:border-white/10 shadow-sm flex flex-col md:flex-row gap-6 items-center md:items-start text-center md:text-right relative overflow-hidden">
					<div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl -ml-16 -mt-16"></div>
					<div className="w-20 h-20 bg-primary/10 text-primary rounded-full flex items-center justify-center font-black text-3xl shrink-0 ring-8 ring-white z-10">
						{employee.name.charAt(0)}
					</div>
					<div className="flex-1 z-10">
						<h2 className="text-2xl font-black text-dark dark:text-gray-100 mb-1">مرحباً، {employee.name} 👋</h2>
						<div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-sm text-gray-500 dark:text-gray-400 font-medium">
							<span className="flex items-center gap-1.5"><Briefcase size={16} className="text-primary" /> {employee.role}</span>
							<span className="flex items-center gap-1.5"><Calendar size={16} className="text-primary" /> انضم ({employee.join_date})</span>
						</div>
						{employee.tasks && (
							<div className="mt-4 p-4 bg-gray-50 dark:bg-white/5 rounded-xl border border-gray-100 dark:border-white/5 text-sm text-gray-600 dark:text-gray-300 text-right">
								<span className="block font-bold text-dark dark:text-gray-100 mb-1">المهام المكلف بها:</span>
								{employee.tasks}
							</div>
						)}
					</div>
				</div>

				{/* Stats Grid */}
				<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
					<div className="bg-white dark:bg-dark-deeper p-5 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm flex flex-col justify-center items-center text-center gap-2 hover:shadow-md transition-all">
						<div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center shrink-0">
							<Video size={24} />
						</div>
						<div>
							<p className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">الفيديوهات المنجزة</p>
							<h3 className="text-2xl font-black text-dark dark:text-gray-100">{stats.total_videos} <span className="text-sm font-medium text-gray-400">فيديو</span></h3>
						</div>
					</div>

					{employee.track_seconds ? (
						<>
							<div className="bg-white dark:bg-dark-deeper p-5 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm flex flex-col justify-center items-center text-center gap-2 hover:shadow-md transition-all">
								<div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center shrink-0">
									<Clock size={24} />
								</div>
								<div>
									<p className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">الثواني المنجزة</p>
									<h3 className="text-2xl font-black text-blue-600">{formatSeconds(totalSecs)}</h3>
								</div>
							</div>
							<div className="bg-white dark:bg-dark-deeper p-5 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm flex flex-col justify-center items-center text-center gap-2 hover:shadow-md transition-all">
								<div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center shrink-0">
									<AlertCircle size={24} />
								</div>
								<div>
									<p className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">تارجت الثواني المتبقية</p>
									<h3 className="text-2xl font-black text-dark dark:text-gray-100">{formatSeconds(remSecs)}</h3>
								</div>
							</div>
							<div className="bg-white dark:bg-dark-deeper p-5 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm flex flex-col justify-center items-center text-center gap-2 hover:shadow-md transition-all relative overflow-hidden">
								{extraSecs > 0 && <div className="absolute top-0 right-0 w-16 h-16 bg-success/10 rounded-full blur-2xl -mr-4 -mt-4"></div>}
								<div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${extraSecs > 0 ? 'bg-success/10 text-success' : 'bg-gray-50 dark:bg-white/5 text-gray-400'}`}>
									<CheckCircle size={24} />
								</div>
								<div className="z-10">
									<p className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-1">الثواني الإضافية (بونص)</p>
									<h3 className={`text-2xl font-black ${extraSecs > 0 ? 'text-success' : 'text-dark dark:text-gray-100'}`}>{formatSeconds(extraSecs)}</h3>
								</div>
							</div>
						</>
					) : (
						<div className="md:col-span-3 bg-white dark:bg-dark-deeper p-5 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm flex items-center gap-4 hover:shadow-md transition-all">
							<div className="w-12 h-12 bg-amber-50 text-amber-600 rounded-xl flex items-center justify-center shrink-0">
								<Clock size={24} />
							</div>
							<div>
								<p className="text-sm font-bold text-gray-500 dark:text-gray-400 mb-1">إجمالي التوقيت المنتج</p>
								<h3 className="text-2xl font-black text-dark dark:text-gray-100">{formatSeconds(totalSecs)}</h3>
							</div>
						</div>
					)}
				</div>

				{employee.track_seconds && (
					<div className="bg-white dark:bg-dark-deeper p-5 rounded-2xl border border-gray-200 dark:border-white/10 shadow-sm">
						<div className="flex justify-between items-end mb-2">
							<div>
								<span className="text-sm font-bold text-gray-600 dark:text-gray-300">معدل الإنجاز للتارجت الشهري ({formatSeconds(baseline)})</span>
							</div>
							<span className={`text-sm font-black ${progress >= 100 ? 'text-success' : 'text-primary'}`}>{Math.round(progress)}%</span>
						</div>
						<div className="bg-gray-100 dark:bg-white/10 rounded-full h-3 overflow-hidden w-full">
							<div className={`h-full rounded-full transition-all duration-500 ${progress >= 100 ? 'bg-success' : 'bg-primary'}`} style={{ width: `${progress}%` }}></div>
						</div>
					</div>
				)}

				{/* Deliverables List */}
				<div className="bg-white dark:bg-dark-deeper rounded-3xl border border-gray-200 dark:border-white/10 shadow-sm overflow-hidden">
					<div className="p-6 border-b border-gray-100 dark:border-white/5 flex items-center justify-between bg-gray-50 dark:bg-white/5">
						<h3 className="text-lg font-black text-dark dark:text-gray-100 flex items-center gap-2">
							<CheckCircle size={20} className="text-primary" />
							سجل التسليمات الخاص بك
						</h3>
						<span className="text-xs font-bold text-primary bg-primary/10 px-3 py-1 rounded-full">
							{deliverables.length} تسليمات
						</span>
					</div>

					{deliverables.length === 0 ? (
						<div className="p-16 text-center">
							<div className="w-16 h-16 bg-gray-50 dark:bg-white/5 rounded-2xl border border-dashed border-gray-200 dark:border-white/10 flex items-center justify-center mx-auto mb-4 text-gray-400">
								<Video size={28} />
							</div>
							<h3 className="text-lg font-bold text-dark dark:text-gray-100 mb-1">لا توجد تسليمات مسجلة</h3>
							<p className="text-sm text-gray-500 dark:text-gray-400">لم يتم تسجيل أي أعمال باسمك بعد.</p>
						</div>
					) : (
						<div className="divide-y divide-gray-100 dark:divide-white/5">
							{deliverables.map((item) => (
								<div key={item.id} className="p-5 sm:p-6 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors flex flex-col sm:flex-row sm:items-center justify-between gap-4">
									<div className="flex gap-4">
										<div className="w-12 h-12 bg-white dark:bg-dark-deeper border shadow-sm border-gray-100 dark:border-white/5 rounded-xl flex items-center justify-center shrink-0 text-primary">
											<Video size={20} />
										</div>
										<div>
											<h4 className="font-bold text-dark dark:text-gray-100 text-base mb-1">{item.title}</h4>
											<div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500 dark:text-gray-400">
												<span className="flex items-center gap-1.5 bg-gray-100 dark:bg-white/10 px-2 py-0.5 rounded-md text-xs font-bold text-gray-600 dark:text-gray-300">
													{item.client_name ? `${item.client_name}${item.package_name ? ` — ${item.package_name}` : ''}` : 'داخلي'}
												</span>
												<span className="flex items-center gap-1"><Calendar size={14} /> {item.delivery_date}</span>
											</div>
										</div>
									</div>
									<div className="flex sm:flex-col gap-3 sm:gap-1 items-center sm:items-end justify-between bg-gray-50 dark:bg-white/5 sm:bg-transparent p-3 sm:p-0 rounded-xl border border-gray-100 dark:border-white/5 sm:border-none">
										<div className="flex gap-4 items-center">
											<div className="text-center">
												<div className="text-xs text-gray-400 font-bold mb-0.5">عدد الفيديوهات</div>
												<div className="font-black text-dark dark:text-gray-100">{item.videos_count}</div>
											</div>
											<div className="h-8 w-px bg-gray-200 dark:bg-white/10"></div>
											<div className="text-center flex flex-col items-center">
												<div className="text-xs text-gray-400 font-bold mb-0.5">مدة الإنتاج</div>
												<div className="font-black text-amber-600 flex items-center gap-1">
													{formatSeconds(item.total_seconds_used)}
													{item.employee_points_multiplier > 1 && (
														<span title={`بونص x${item.employee_points_multiplier}`} style={{ background: '#FEF3C7', color: '#B45309', padding: '0px 4px', borderRadius: 4, fontSize: 10 }}>⚡x{item.employee_points_multiplier}</span>
													)}
												</div>
											</div>
										</div>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			{/* Footer */}
			<div className="py-8 text-center text-sm font-medium text-gray-400">
				<p>جميع الحقوق محفوظة © {new Date().getFullYear()} — MS MEDIA BOARD</p>
				<p className="text-xs mt-1 opacity-70">تم توفير هذا الرابط لك شخصياً لمتابعة أدائك الوظيفي</p>
			</div>
		</div>
	);
}
